import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './common/LanguageSwitcher';
import LoginModal from './LoginModal';
import './ModuleExamsPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const ModuleExamsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('landing');
  const { level, module, stream, year } = useParams();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(year || null);

  useEffect(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    setIsAuthenticated(!!token);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Sync selectedYear with URL param
    if (year) {
      setSelectedYear(year);
    } else {
      setSelectedYear(null);
    }
  }, [year]);

  useEffect(() => {
    if (selectedYear) {
      fetchExams();
    }
  }, [level, module, stream, selectedYear]);

  const fetchExams = async () => {
    if (!selectedYear) return;
    
    setLoading(true);
    try {
      const examLevel = stream ? 'high' : level;
      const examYear = selectedYear;
      const response = await fetch(
        `${API_BASE_URL}/api/exams?level=${examLevel}&year=${examYear}&module=${module}${stream ? `&stream=${stream}` : ''}`
      );
      const data = await response.json();
      if (data.success) {
        setExams(data.exams || []);
      }
    } catch (error) {
      console.error('Error fetching exams:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = (exam, isCorrection = false) => {
    const url = isCorrection
      ? `${API_BASE_URL}/api/exams/${exam.id}/download/pdf?type=correction`
      : `${API_BASE_URL}/api/exams/${exam.id}/download/pdf`;
    window.open(url, '_blank');
  };

  const handleDownloadWord = (exam, isCorrection = false) => {
    if (isAuthenticated) {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const url = isCorrection
        ? `${API_BASE_URL}/api/exams/${exam.id}/download/word?token=${token}&type=correction`
        : `${API_BASE_URL}/api/exams/${exam.id}/download/word?token=${token}`;
      window.open(url, '_blank');
    } else {
      setPendingDownload({ ...exam, isCorrection });
      setShowLoginModal(true);
    }
  };

  const handleLoginSuccess = (user) => {
    setIsAuthenticated(true);
    setShowLoginModal(false);
    if (pendingDownload) {
      setTimeout(() => {
        handleDownloadWord(pendingDownload, pendingDownload.isCorrection || false);
        setPendingDownload(null);
      }, 500);
    }
  };

  const scrollToSection = (id) => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  // Get module name and available years based on level
  const getModuleInfo = () => {
    const moduleNames = {
      'arabic': 'Arabic',
      'mathematics': 'Mathematics',
      'math': 'Mathematics',
      'english': 'English',
      'french': 'French',
      'science': 'Science & Technology',
      'natural-sciences': 'Natural Sciences',
      'physical-sciences': 'Physical Sciences',
      'islamic-education': 'Islamic Education',
      'history-geography': 'History & Geography',
      'civic-education': 'Civic Education',
      'informatics': 'Informatics',
      'philosophy': 'Philosophy',
      'management': 'Management',
      'economics': 'Economics',
      'accounting': 'Accounting',
      'law': 'Law',
      'mechanical-technology': 'Mechanical Technology',
      'electrical-technology': 'Electrical Technology'
    };

    const yearsByLevel = {
      'primary': ['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5'],
      'middle': ['1AM', '2AM', '3AM', '4AM', 'BEM'],
      'high': ['1AS', '2AS', 'BAC']
    };

    return {
      name: moduleNames[module] || module,
      years: stream ? yearsByLevel['high'] : yearsByLevel[level] || []
    };
  };

  const moduleInfo = getModuleInfo();
  const schoolName = level === 'primary' ? 'Primary School' : level === 'middle' ? 'Middle School' : 'High School';
  
  // Get stream display name
  const streamNames = {
    'sciences': 'Sciences',
    'math': 'Mathematics',
    'literature': 'Literature',
    'technical-math': 'Technical Mathematics',
    'management-economics': 'Management & Economics'
  };
  const streamDisplayName = stream ? streamNames[stream] || stream : null;

  return (
    <div className="module-exams-page">
      {/* Navigation Bar */}
      <nav className={`landing-navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="navbar-container">
          <div className="logo" onClick={() => navigate('/')}>
            <span className="material-icons">school</span>
            <span>Nativelike</span>
          </div>
          <div className={`nav-menu ${isMobileMenuOpen ? 'open' : ''}`}>
            <a href="#features" onClick={(e) => { e.preventDefault(); scrollToSection('features'); setIsMobileMenuOpen(false); }}>{t('nav.features')}</a>
            <a href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollToSection('how-it-works'); setIsMobileMenuOpen(false); }}>{t('nav.howItWorks')}</a>
            <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollToSection('pricing'); setIsMobileMenuOpen(false); }}>{t('nav.pricing')}</a>
            <a href="#testimonials" onClick={(e) => { e.preventDefault(); scrollToSection('testimonials'); setIsMobileMenuOpen(false); }}>{t('nav.reviews')}</a>
            <a href="/resources" onClick={(e) => { e.preventDefault(); navigate('/resources'); setIsMobileMenuOpen(false); }} className="active">Resources</a>
          </div>
          <div className="nav-actions">
            <LanguageSwitcher className="landing-language-switcher" />
            <button className="btn-login" onClick={() => scrollToSection('login')}>{t('nav.login')}</button>
            <button className="btn-primary" onClick={() => navigate('/signup')}>{t('nav.startLearning')}</button>
            <button className="mobile-menu-toggle" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
              <span className="material-icons">{isMobileMenuOpen ? 'close' : 'menu'}</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="module-exams-container" style={{ paddingTop: '80px' }}>
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <a href="/resources" onClick={(e) => { e.preventDefault(); navigate('/resources'); }}>Resources</a>
          <span className="material-icons">chevron_right</span>
          <a href={`/resources/${level}${stream ? `/high/${stream}` : ''}`} onClick={(e) => { e.preventDefault(); navigate(`/resources/${level}${stream ? `/high/${stream}` : ''}`); }}>
            {schoolName}{streamDisplayName ? ` - ${streamDisplayName}` : ''}
          </a>
          <span className="material-icons">chevron_right</span>
          <a href={`/resources/${level}${stream ? `/high/${stream}` : ''}/${module}`} onClick={(e) => { e.preventDefault(); navigate(`/resources/${level}${stream ? `/high/${stream}` : ''}/${module}`); }}>
            {moduleInfo.name}
          </a>
          {selectedYear && (
            <>
              <span className="material-icons">chevron_right</span>
              <span>{selectedYear}</span>
            </>
          )}
        </div>

        {/* Header */}
        <div className="module-header">
          <h1 className="module-title">{moduleInfo.name}</h1>
          <p className="module-subtitle">
            {schoolName}{streamDisplayName ? ` - ${streamDisplayName}` : ''}
            {selectedYear && ` - ${selectedYear}`}
          </p>
        </div>

        {/* Year Selection */}
        {!selectedYear && (
          <div className="years-selection-section">
            <h2 className="section-title">Select Year / Level</h2>
            <div className="years-grid">
              {moduleInfo.years.map((yearOption) => (
                <button
                  key={yearOption}
                  className="year-button"
                  onClick={() => {
                    setSelectedYear(yearOption);
                    const newPath = stream
                      ? `/resources/high/${stream}/${module}/${yearOption}`
                      : `/resources/${level}/${module}/${yearOption}`;
                    navigate(newPath);
                  }}
                >
                  {yearOption}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Exams List */}
        {selectedYear && (
          <div className="exams-section">
            <div className="exams-header">
              <h2 className="section-title">
                {moduleInfo.name} - {selectedYear}
              </h2>
              <button className="btn-change-year" onClick={() => setSelectedYear(null)}>
                Change Year
              </button>
            </div>
            
            {loading ? (
              <div className="loading-state">
                <span className="material-icons">hourglass_empty</span>
                <p>Loading exams...</p>
              </div>
            ) : exams.length === 0 ? (
              <div className="empty-state">
                <span className="material-icons">description</span>
                <p>No exams available for {moduleInfo.name} - {selectedYear} yet.</p>
                <p className="empty-state-note">Check back soon for new content!</p>
              </div>
            ) : (
              <div className="exams-grid">
                {exams.map((exam) => (
                  <div key={exam.id} className="exam-card">
                    <div className="exam-header">
                      <span className="material-icons">description</span>
                      <h3>{exam.title}</h3>
                    </div>
                    <div className="exam-meta">
                      <span className="exam-date">
                        <span className="material-icons">calendar_today</span>
                        {new Date(exam.date).toLocaleDateString()}
                      </span>
                    </div>
                    {exam.description && (
                      <div className="exam-description">
                        {exam.description}
                      </div>
                    )}
                    <div className="exam-actions">
                      <button
                        className="btn-download-pdf"
                        onClick={() => handleDownloadPDF(exam)}
                      >
                        <span className="material-icons">picture_as_pdf</span>
                        Download PDF (Free)
                      </button>
                      <button
                        className="btn-download-word"
                        onClick={() => handleDownloadWord(exam)}
                      >
                        <span className="material-icons">lock</span>
                        Download Word (Login Required)
                      </button>
                    </div>
                    {exam.hasCorrection && (
                      <div className="exam-corrections">
                        <h4>Corrections:</h4>
                        <div className="correction-actions">
                          <button
                            className="btn-download-pdf-small"
                            onClick={() => handleDownloadPDF(exam, true)}
                          >
                            <span className="material-icons">picture_as_pdf</span>
                            PDF (Free)
                          </button>
                          <button
                            className="btn-download-word-small"
                            onClick={() => handleDownloadWord(exam, true)}
                          >
                            <span className="material-icons">lock</span>
                            Word (Login)
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => {
          setShowLoginModal(false);
          setPendingDownload(null);
        }}
        onLoginSuccess={handleLoginSuccess}
      />
    </div>
  );
};

export default ModuleExamsPage;
